import React from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import SoupKitchenRoundedIcon from "@mui/icons-material/SoupKitchenRounded";
import LocalLaundryServiceRoundedIcon from "@mui/icons-material/LocalLaundryServiceRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import WorkerData from "../../Types/WorkerData";
import { Permissions } from "../../Types/Enums/Permissions";
import { Functions } from "../../Types/Enums/Functions";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import AuthRepository from "../../Repositories/AuthRepository";
import AreYouSureDialog from "../Dialogs/AreYouSureDialog";

interface Props {
    worker: WorkerData | null;
    onClose: () => void;
    onWorkerUnlinked?: () => void;
    onAccountDeleted?: () => void;
    onUserCreated?: (worker: WorkerData) => void;
}

interface State {
    worker: WorkerData | null;
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
    showCurrent: boolean;
    showNew: boolean;
    showConfirm: boolean;
    passwordError: string;
    passwordSuccess: boolean;
    isSavingPassword: boolean;
    showUnlinkDialog: boolean;
    showDeleteDialog: boolean;
    createUsername: string;
    createPassword: string;
    createConfirmPassword: string;
    showCreatePassword: boolean;
    showCreateConfirm: boolean;
    createError: string;
    isCreatingUser: boolean;
    createSuccess: boolean;
}

export default class UserDetailsDialog extends React.Component<Props, State> {
    static contextType = AuthContext;
    context!: AuthContextType;

    private readonly ACCENT = "#c79656";
    private readonly ACCENT_LIGHT = "rgba(199,150,86,0.10)";
    private readonly ACCENT_GLOW = "rgba(199,150,86,0.22)";
    private readonly SURFACE = "#ffffff";
    private readonly BORDER = "rgba(0,0,0,0.07)";
    private readonly TEXT_PRIMARY = "#18181b";
    private readonly TEXT_SECONDARY = "rgba(24,24,27,0.52)";
    private readonly DANGER = "#d1180a";
    private readonly DANGER_LIGHT = "rgba(209,24,10,0.08)";
    private readonly DISABLED_BG = "rgba(0,0,0,0.025)";
    private readonly DISABLED_TEXT = "rgba(24,24,27,0.28)";

    constructor(props: Props) {
        super(props);

        this.onPasswordSave = this.onPasswordSave.bind(this);
        this.onUnlinkConfirm = this.onUnlinkConfirm.bind(this);
        this.onDeleteConfirm = this.onDeleteConfirm.bind(this);
        this.onCreateUser = this.onCreateUser.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);

        this.state = {
            worker: props.worker,
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
            showCurrent: false,
            showNew: false,
            showConfirm: false,
            passwordError: "",
            passwordSuccess: false,
            isSavingPassword: false,
            showUnlinkDialog: false,
            showDeleteDialog: false,
            createUsername: "",
            createPassword: "",
            createConfirmPassword: "",
            showCreatePassword: false,
            showCreateConfirm: false,
            createError: "",
            isCreatingUser: false,
            createSuccess: false,
        };
    }

    private handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
        if (e.target === e.currentTarget) {
            this.props.onClose();
        }
    }

    private get hasUser(): boolean {
        return !!this.state.worker?.UserId;
    }

    private async onPasswordSave() {
        const { currentPassword, newPassword, confirmPassword } = this.state;

        if (!currentPassword || !newPassword || !confirmPassword) {
            this.setState({ passwordError: "All fields are required." });
            return;
        }
        if (newPassword.length < 8) {
            this.setState({ passwordError: "New password must be at least 8 characters." });
            return;
        }
        if (newPassword !== confirmPassword) {
            this.setState({ passwordError: "New passwords do not match." });
            return;
        }

        this.setState({ isSavingPassword: true, passwordError: "" });

        const result = await AuthRepository.changePassword(this.state.worker!.UserId, currentPassword, newPassword);
        if (result.isFailed()) {
            this.setState({ isSavingPassword: false, passwordError: result.errorMessage ?? "Failed to update password." });
            return;
        }

        if (result.result!.errorMessage !== null) {
            this.setState({ isSavingPassword: false, passwordError: result.result!.errorMessage ?? "Something went wrong." });
            return;
        }

        this.setState({
            isSavingPassword: false,
            passwordSuccess: true,
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        });

        setTimeout(() => this.setState({ passwordSuccess: false }), 3000);
    }

    private async onCreateUser() {
        const { createUsername, createPassword, createConfirmPassword, worker } = this.state;

        if (!createUsername.trim() || !createPassword || !createConfirmPassword) {
            this.setState({ createError: "All fields are required." });
            return;
        }
        if (createPassword.length < 8) {
            this.setState({ createError: "Password must be at least 8 characters." });
            return;
        }
        if (createPassword !== createConfirmPassword) {
            this.setState({ createError: "Passwords do not match." });
            return;
        }

        this.setState({ isCreatingUser: true, createError: "" });

        const result = await AuthRepository.signup(
            createUsername.trim(),
            createPassword,
            worker?.Guid ?? "",
            "",
            this.context.locationId
        );

        if (result.isFailed()) {
            this.setState({ isCreatingUser: false, createError: result.errorMessage ?? "Failed to create account." });
            return;
        }

        if (result.result!.userId === null) {
            this.setState({ isCreatingUser: false, createError: result.result!.errorMessage ?? "Something went wrong." });
            return;
        }

        const updatedWorker = this.state.worker;
        if (updatedWorker) {
            updatedWorker.UserId = result.result?.userId ?? "";
            updatedWorker.Username = createUsername.trim();
        }

        this.setState({
            isCreatingUser: false,
            createSuccess: true,
            worker: updatedWorker,
            createUsername: "",
            createPassword: "",
            createConfirmPassword: "",
        });

        if (updatedWorker) {
            this.props.onUserCreated?.(updatedWorker);
        }

        setTimeout(() => this.setState({ createSuccess: false }), 3000);
    }

    private async onUnlinkConfirm(accepted: boolean) {
        if (accepted && this.state.worker) {
            // const result = await WorkerRepository.unlinkWorker(this.context.locationId, this.state.worker.Guid);
            // if (result.isFailed()) {
            //     this.setState({ showUnlinkDialog: false });
            //     return;
            // }
            this.setState({ showUnlinkDialog: false, worker: null });
            this.props.onWorkerUnlinked?.();
        } else {
            this.setState({ showUnlinkDialog: false });
        }
    }

    private async onDeleteConfirm(accepted: boolean) {
        if (accepted) {
            // await AuthRepository.deleteAccount(this.props.userId);
            this.props.onAccountDeleted?.();
        } else {
            this.setState({ showDeleteDialog: false });
        }
    }

    private functionMeta(f: Functions) {
        switch (f) {
            case Functions.Server:
                return { label: "Server", Icon: RestaurantRoundedIcon };
            case Functions.Kitchen:
                return { label: "Kitchen", Icon: SoupKitchenRoundedIcon };
            case Functions.Dishwashing:
                return { label: "Dishwashing", Icon: LocalLaundryServiceRoundedIcon };
            default:
                return { label: "Unknown", Icon: RestaurantRoundedIcon };
        }
    }

    private permissionMeta(p: Permissions) {
        type Tone = "neutral" | "accent" | "dark";
        switch (p) {
            case Permissions.User:
                return { label: "User", Icon: PersonOutlineRoundedIcon, tone: "neutral" as Tone };
            case Permissions.Administrator:
                return { label: "Admin", Icon: AdminPanelSettingsRoundedIcon, tone: "accent" as Tone };
            case Permissions.Developer:
                return { label: "Developer", Icon: CodeRoundedIcon, tone: "dark" as Tone };
            default:
                return { label: "Unknown", Icon: PersonOutlineRoundedIcon, tone: "neutral" as Tone };
        }
    }

    private permPillStyle(tone: "neutral" | "accent" | "dark"): React.CSSProperties {
        const tones = {
            neutral: { bg: "rgba(0,0,0,0.05)", fg: "rgba(24,24,27,0.72)", bd: "1px solid rgba(0,0,0,0.08)", icon: "rgba(24,24,27,0.45)" },
            accent: { bg: this.ACCENT_LIGHT, fg: "#a0722a", bd: `1px solid rgba(199,150,86,0.28)`, icon: this.ACCENT },
            dark: { bg: "rgba(24,24,27,0.90)", fg: "#fff", bd: "1px solid rgba(255,255,255,0.08)", icon: "rgba(255,255,255,0.65)" },
        }[tone];

        return {
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px 3px 5px",
            borderRadius: 5,
            background: tones.bg,
            color: tones.fg,
            border: tones.bd,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: "nowrap",
            ["--icon-color" as any]: tones.icon,
        };
    }

    private Section = ({
        label,
        icon,
        children,
        disabled,
        disabledReason,
    }: {
        label: string;
        icon: React.ReactNode;
        children: React.ReactNode;
        disabled?: boolean;
        disabledReason?: string;
    }) => (
        <div style={{ display: "flex", flexDirection: "column", opacity: disabled ? 0.45 : 1, transition: "opacity 150ms ease" }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    paddingBottom: 10,
                    marginBottom: 14,
                    borderBottom: `1px solid ${this.BORDER}`,
                }}
            >
                <span style={{ display: "flex", alignItems: "center", color: disabled ? this.DISABLED_TEXT : this.TEXT_SECONDARY }}>
                    {icon}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: disabled ? this.DISABLED_TEXT : this.TEXT_PRIMARY, letterSpacing: 0.1 }}>
                    {label}
                </span>
                {disabled && disabledReason && (
                    <span
                        style={{
                            marginLeft: "auto",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            color: this.DISABLED_TEXT,
                            background: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(0,0,0,0.07)",
                            borderRadius: 5,
                            padding: "2px 7px",
                            letterSpacing: 0.2,
                        }}
                    >
                        <LockOutlinedIcon style={{ fontSize: 10 }} />
                        {disabledReason}
                    </span>
                )}
            </div>
            <div style={{ pointerEvents: disabled ? "none" : "auto", userSelect: disabled ? "none" : "auto" }}>
                {children}
            </div>
        </div>
    );

    private Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    color: this.TEXT_SECONDARY,
                }}
            >
                {label}
            </span>
            <div style={{ fontSize: 13.5, fontWeight: 500, color: this.TEXT_PRIMARY }}>
                {value}
            </div>
        </div>
    );

    private PasswordInput = ({
        label,
        value,
        visible,
        onChange,
        onToggle,
        autoComplete,
        disabled,
    }: {
        label: string;
        value: string;
        visible: boolean;
        onChange: (v: string) => void;
        onToggle: () => void;
        autoComplete?: string;
        disabled?: boolean;
    }) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    color: this.TEXT_SECONDARY,
                }}
            >
                {label}
            </label>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    border: `1.5px solid rgba(0,0,0,0.12)`,
                    borderRadius: 8,
                    background: disabled ? this.DISABLED_BG : "rgba(0,0,0,0.015)",
                    overflow: "hidden",
                    transition: "border-color 120ms ease",
                }}
                onFocusCapture={(e) => { if (!disabled) e.currentTarget.style.borderColor = this.ACCENT; }}
                onBlurCapture={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)")}
            >
                <input
                    type={visible ? "text" : "password"}
                    value={value}
                    autoComplete={autoComplete}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        flex: 1,
                        border: "none",
                        background: "transparent",
                        padding: "8px 12px",
                        fontSize: 13,
                        color: disabled ? this.DISABLED_TEXT : this.TEXT_PRIMARY,
                        outline: "none",
                        fontFamily: "inherit",
                        cursor: disabled ? "not-allowed" : "text",
                    }}
                />
                <button
                    type="button"
                    onClick={onToggle}
                    disabled={disabled}
                    style={{
                        appearance: "none",
                        background: "transparent",
                        border: "none",
                        padding: "0 10px",
                        cursor: disabled ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        color: this.TEXT_SECONDARY,
                    }}
                >
                    {visible
                        ? <VisibilityOffRoundedIcon style={{ fontSize: 17 }} />
                        : <VisibilityRoundedIcon style={{ fontSize: 17 }} />
                    }
                </button>
            </div>
        </div>
    );

    private TextInput = ({
        label,
        value,
        onChange,
        placeholder,
        autoComplete,
    }: {
        label: string;
        value: string;
        onChange: (v: string) => void;
        placeholder?: string;
        autoComplete?: string;
    }) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    color: this.TEXT_SECONDARY,
                }}
            >
                {label}
            </label>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    border: `1.5px solid rgba(0,0,0,0.12)`,
                    borderRadius: 8,
                    background: "rgba(0,0,0,0.015)",
                    overflow: "hidden",
                    transition: "border-color 120ms ease",
                }}
                onFocusCapture={(e) => (e.currentTarget.style.borderColor = this.ACCENT)}
                onBlurCapture={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)")}
            >
                <input
                    type="text"
                    value={value}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        flex: 1,
                        border: "none",
                        background: "transparent",
                        padding: "8px 12px",
                        fontSize: 13,
                        color: this.TEXT_PRIMARY,
                        outline: "none",
                        fontFamily: "inherit",
                    }}
                />
            </div>
        </div>
    );

    render() {
        const {
            worker,
            currentPassword,
            newPassword,
            confirmPassword,
            showCurrent,
            showNew,
            showConfirm,
            passwordError,
            passwordSuccess,
            isSavingPassword,
            createUsername,
            createPassword,
            createConfirmPassword,
            showCreatePassword,
            showCreateConfirm,
            createError,
            isCreatingUser,
            createSuccess,
        } = this.state;

        const hasUser = this.hasUser;
        const username = worker?.Username ?? "No account";
        const userId = worker?.UserId ?? "";

        const initials = worker
            ? `${worker.FirstName.charAt(0)}${worker.LastName.charAt(0)}`.toUpperCase()
            : null;

        const func = worker ? this.functionMeta(worker.Function) : null;
        const perm = worker ? this.permissionMeta(worker.Permission) : null;

        return (
            <>
                <div
                    onClick={this.handleBackdropClick}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.35)",
                        backdropFilter: "blur(3px)",
                        zIndex: 1200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 24,
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            maxWidth: 680,
                            maxHeight: "90vh",
                            background: this.SURFACE,
                            borderRadius: 16,
                            boxShadow: "0 8px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "16px 20px",
                                borderBottom: `1px solid ${this.BORDER}`,
                                flexShrink: 0,
                            }}
                        >
                            <div
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    background: hasUser ? this.ACCENT_LIGHT : "rgba(0,0,0,0.05)",
                                    color: hasUser ? "#9a6f2e" : this.DISABLED_TEXT,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 12,
                                    fontWeight: 750,
                                    marginRight: 10,
                                    border: `1px solid ${hasUser ? "rgba(199,150,86,0.18)" : "rgba(0,0,0,0.07)"}`,
                                    flexShrink: 0,
                                }}
                            >
                                {hasUser ? username.charAt(0).toUpperCase() : <PersonRoundedIcon style={{ fontSize: 16 }} />}
                            </div>
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: this.TEXT_PRIMARY, lineHeight: 1.2 }}>
                                    {worker ? `${worker.FirstName} ${worker.LastName}` : "Account"}
                                </div>
                                <div style={{ fontSize: 11, color: this.TEXT_SECONDARY, marginTop: 1 }}>
                                    {hasUser ? `@${username}` : "No account linked"}
                                </div>
                            </div>
                            <div style={{ flex: 1 }} />
                            <IconButton
                                size="small"
                                onClick={this.props.onClose}
                                style={{ borderRadius: 8, padding: 5, color: this.TEXT_SECONDARY }}
                            >
                                <CloseRoundedIcon style={{ fontSize: 18 }} />
                            </IconButton>
                        </div>

                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "24px 24px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 28,
                                overscrollBehavior: "contain",
                            }}
                        >
                            <this.Section
                                label="Account details"
                                icon={<PersonRoundedIcon style={{ fontSize: 14 }} />}
                                disabled={!hasUser}
                                disabledReason="No account"
                            >
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                    <this.Field
                                        label="Username"
                                        value={
                                            <span style={{ fontFamily: "monospace", fontSize: 13 }}>
                                                @{username}
                                            </span>
                                        }
                                    />
                                    <this.Field
                                        label="User ID"
                                        value={
                                            <span style={{ fontFamily: "monospace", fontSize: 11, color: this.TEXT_SECONDARY, wordBreak: "break-all" }}>
                                                {userId || "—"}
                                            </span>
                                        }
                                    />
                                </div>
                            </this.Section>

                            <this.Section
                                label="Change password"
                                icon={<LockResetRoundedIcon style={{ fontSize: 14 }} />}
                                disabled={!hasUser}
                                disabledReason="No account"
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                    <this.PasswordInput
                                        label="Current password"
                                        value={currentPassword}
                                        visible={showCurrent}
                                        autoComplete="current-password"
                                        disabled={!hasUser}
                                        onChange={(v) => this.setState({ currentPassword: v, passwordError: "", passwordSuccess: false })}
                                        onToggle={() => this.setState((s) => ({ showCurrent: !s.showCurrent }))}
                                    />
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                        <this.PasswordInput
                                            label="New password"
                                            value={newPassword}
                                            visible={showNew}
                                            autoComplete="new-password"
                                            disabled={!hasUser}
                                            onChange={(v) => this.setState({ newPassword: v, passwordError: "", passwordSuccess: false })}
                                            onToggle={() => this.setState((s) => ({ showNew: !s.showNew }))}
                                        />
                                        <this.PasswordInput
                                            label="Confirm new password"
                                            value={confirmPassword}
                                            visible={showConfirm}
                                            autoComplete="new-password"
                                            disabled={!hasUser}
                                            onChange={(v) => this.setState({ confirmPassword: v, passwordError: "", passwordSuccess: false })}
                                            onToggle={() => this.setState((s) => ({ showConfirm: !s.showConfirm }))}
                                        />
                                    </div>

                                    {passwordError && (
                                        <div
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 500,
                                                color: this.DANGER,
                                                background: this.DANGER_LIGHT,
                                                border: `1px solid rgba(209,24,10,0.15)`,
                                                borderRadius: 7,
                                                padding: "8px 12px",
                                            }}
                                        >
                                            {passwordError}
                                        </div>
                                    )}

                                    {passwordSuccess && (
                                        <div
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 500,
                                                color: "#1e7e34",
                                                background: "rgba(30,126,52,0.08)",
                                                border: `1px solid rgba(30,126,52,0.18)`,
                                                borderRadius: 7,
                                                padding: "8px 12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                            }}
                                        >
                                            <CheckRoundedIcon style={{ fontSize: 14 }} />
                                            Password updated successfully.
                                        </div>
                                    )}

                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            disabled={isSavingPassword || !hasUser}
                                            onClick={this.onPasswordSave}
                                            style={{
                                                textTransform: "none",
                                                fontWeight: 700,
                                                fontSize: 13,
                                                borderRadius: 9,
                                                paddingInline: 18,
                                                paddingBlock: 7,
                                                backgroundColor: (isSavingPassword || !hasUser) ? "rgba(0,0,0,0.08)" : this.ACCENT,
                                                boxShadow: (isSavingPassword || !hasUser) ? "none" : `0 2px 8px ${this.ACCENT_GLOW}`,
                                                color: (isSavingPassword || !hasUser) ? this.TEXT_SECONDARY : "#fff",
                                                letterSpacing: 0.1,
                                            }}
                                        >
                                            {isSavingPassword ? "Saving…" : "Update password"}
                                        </Button>
                                    </div>
                                </div>
                            </this.Section>

                            <this.Section
                                label="Linked worker"
                                icon={<LinkRoundedIcon style={{ fontSize: 14 }} />}
                            >
                                {!worker ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 7,
                                            padding: "24px 0",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: "50%",
                                                background: "rgba(0,0,0,0.04)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <BadgeRoundedIcon style={{ fontSize: 18, color: "rgba(0,0,0,0.22)" }} />
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(0,0,0,0.35)" }}>
                                            No worker linked to this account
                                        </div>
                                    </div>
                                ) : func && perm ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 12,
                                                padding: "12px 14px",
                                                borderRadius: 10,
                                                border: `1px solid ${this.BORDER}`,
                                                background: "rgba(0,0,0,0.012)",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 9,
                                                    background: this.ACCENT_LIGHT,
                                                    color: "#9a6f2e",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 13,
                                                    fontWeight: 750,
                                                    flexShrink: 0,
                                                    border: `1px solid rgba(199,150,86,0.18)`,
                                                }}
                                            >
                                                {initials}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13.5, fontWeight: 700, color: this.TEXT_PRIMARY, lineHeight: 1.2 }}>
                                                    {worker.FirstName} {worker.LastName}
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, flexWrap: "wrap" }}>
                                                    <div
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 4,
                                                            padding: "3px 8px 3px 5px",
                                                            borderRadius: 5,
                                                            border: `1px solid rgba(199,150,86,0.25)`,
                                                            background: this.ACCENT_LIGHT,
                                                            color: "#8a5e1a",
                                                            fontWeight: 600,
                                                            fontSize: 11,
                                                            lineHeight: 1,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        <func.Icon style={{ fontSize: 12, color: this.ACCENT }} />
                                                        <span>{func.label}</span>
                                                    </div>
                                                    <div style={this.permPillStyle(perm.tone)}>
                                                        <perm.Icon style={{ fontSize: 12, color: "var(--icon-color)" as any }} />
                                                        <span>{perm.label}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {hasUser && (
                                                <Tooltip title="Unlink worker" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => this.setState({ showUnlinkDialog: true })}
                                                        style={{
                                                            borderRadius: 8,
                                                            padding: 6,
                                                            color: this.TEXT_SECONDARY,
                                                            border: `1px solid ${this.BORDER}`,
                                                            background: this.SURFACE,
                                                        }}
                                                    >
                                                        <LinkOffRoundedIcon style={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </this.Section>

                            {!hasUser && worker && (
                                <this.Section
                                    label="Create account"
                                    icon={<PersonAddRoundedIcon style={{ fontSize: 14 }} />}
                                >
                                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: this.TEXT_SECONDARY,
                                                background: "rgba(0,0,0,0.025)",
                                                border: `1px solid ${this.BORDER}`,
                                                borderRadius: 8,
                                                padding: "9px 12px",
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            This worker has no account yet. Create one below to allow them to log in. The account will be automatically linked to <strong style={{ color: this.TEXT_PRIMARY, fontWeight: 700 }}>{worker.FirstName} {worker.LastName}</strong>.
                                        </div>

                                        <this.TextInput
                                            label="Username"
                                            value={createUsername}
                                            placeholder="e.g. john.doe"
                                            autoComplete="off"
                                            onChange={(v) => this.setState({ createUsername: v, createError: "", createSuccess: false })}
                                        />

                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                            <this.PasswordInput
                                                label="Password"
                                                value={createPassword}
                                                visible={showCreatePassword}
                                                autoComplete="new-password"
                                                onChange={(v) => this.setState({ createPassword: v, createError: "", createSuccess: false })}
                                                onToggle={() => this.setState((s) => ({ showCreatePassword: !s.showCreatePassword }))}
                                            />
                                            <this.PasswordInput
                                                label="Confirm password"
                                                value={createConfirmPassword}
                                                visible={showCreateConfirm}
                                                autoComplete="new-password"
                                                onChange={(v) => this.setState({ createConfirmPassword: v, createError: "", createSuccess: false })}
                                                onToggle={() => this.setState((s) => ({ showCreateConfirm: !s.showCreateConfirm }))}
                                            />
                                        </div>

                                        {createError && (
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: this.DANGER,
                                                    background: this.DANGER_LIGHT,
                                                    border: `1px solid rgba(209,24,10,0.15)`,
                                                    borderRadius: 7,
                                                    padding: "8px 12px",
                                                }}
                                            >
                                                {createError}
                                            </div>
                                        )}

                                        {createSuccess && (
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: "#1e7e34",
                                                    background: "rgba(30,126,52,0.08)",
                                                    border: `1px solid rgba(30,126,52,0.18)`,
                                                    borderRadius: 7,
                                                    padding: "8px 12px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                }}
                                            >
                                                <CheckRoundedIcon style={{ fontSize: 14 }} />
                                                Account created and linked successfully.
                                            </div>
                                        )}

                                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <Button
                                                variant="contained"
                                                disableElevation
                                                disabled={isCreatingUser}
                                                startIcon={<PersonAddRoundedIcon style={{ fontSize: 16 }} />}
                                                onClick={this.onCreateUser}
                                                style={{
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    fontSize: 13,
                                                    borderRadius: 9,
                                                    paddingInline: 18,
                                                    paddingBlock: 7,
                                                    backgroundColor: isCreatingUser ? "rgba(0,0,0,0.08)" : this.ACCENT,
                                                    boxShadow: isCreatingUser ? "none" : `0 2px 8px ${this.ACCENT_GLOW}`,
                                                    color: isCreatingUser ? this.TEXT_SECONDARY : "#fff",
                                                    letterSpacing: 0.1,
                                                }}
                                            >
                                                {isCreatingUser ? "Creating…" : "Create & link account"}
                                            </Button>
                                        </div>
                                    </div>
                                </this.Section>
                            )}

                            <div
                                style={{
                                    borderRadius: 10,
                                    border: `1px solid ${hasUser ? "rgba(209,24,10,0.18)" : "rgba(0,0,0,0.07)"}`,
                                    background: hasUser ? this.DANGER_LIGHT : this.DISABLED_BG,
                                    padding: "14px 16px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 16,
                                    opacity: hasUser ? 1 : 0.45,
                                    transition: "opacity 150ms ease",
                                }}
                            >
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: hasUser ? this.DANGER : this.DISABLED_TEXT, lineHeight: 1.2 }}>
                                        Delete account
                                    </div>
                                    <div style={{ fontSize: 11.5, color: hasUser ? "rgba(209,24,10,0.75)" : this.DISABLED_TEXT, marginTop: 2 }}>
                                        Permanently removes your account. This cannot be undone.
                                    </div>
                                </div>
                                <Button
                                    variant="outlined"
                                    disableElevation
                                    disabled={!hasUser}
                                    startIcon={<DeleteOutlineRoundedIcon style={{ fontSize: 15 }} />}
                                    onClick={() => this.setState({ showDeleteDialog: true })}
                                    style={{
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: 12,
                                        borderRadius: 8,
                                        paddingInline: 14,
                                        paddingBlock: 6,
                                        color: hasUser ? this.DANGER : this.DISABLED_TEXT,
                                        borderColor: hasUser ? "rgba(209,24,10,0.35)" : "rgba(0,0,0,0.1)",
                                        background: "#fff",
                                        flexShrink: 0,
                                        letterSpacing: 0.1,
                                        pointerEvents: hasUser ? "auto" : "none",
                                    }}
                                >
                                    Delete account
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.showUnlinkDialog && (
                    <AreYouSureDialog
                        message={`Unlink ${worker?.FirstName} ${worker?.LastName} from your account?`}
                        onClose={this.onUnlinkConfirm}
                    />
                )}

                {this.state.showDeleteDialog && (
                    <AreYouSureDialog
                        message="Are you sure you want to permanently delete your account? This cannot be undone."
                        onClose={this.onDeleteConfirm}
                    />
                )}
            </>
        );
    }
}